// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The state machine: ingest → parse (in a worker) → size → render.
 *
 * Everything downstream of the parse is pure and cheap, so changing the
 * context slider or the cache type re-runs the whole calculation
 * synchronously — no refetch, no spinner.
 */

import './styles/main.css';

import { Template } from '@huggingface/jinja';
import {
  buildLlamaServerCommand,
  buildModelfile,
  buildReportJson,
  reconcile,
  type Reconciliation,
} from './artefacts';
import { clearLog, eventCount, log, mountEventDrawer } from './eventlog';
import {
  bytesToGiB,
  escapeHtml,
  formatBytes,
  formatBpw,
  formatContext,
  formatParams,
  formatPercent,
  gibToBytes,
  slugify,
} from './format';
import { GeometryError, resolveGeometry, type Geometry } from './geometry';
import { metaNumber, metaString } from './gguf/parse';
import { groupShards, hfResolveUrl, listRepoGgufs, parseTarget, type RepoFile } from './gguf/source';
import { NotGguf, type GgufValue } from './gguf/types';
import {
  isSuggestDisabled,
  searchGgufRepos,
  shouldSuggest,
  suggestDisabledReason,
  type ModelHit,
} from './hfsearch';
import type { InspectProgress, InspectResult, InspectTarget } from './inspect';
import { inspect } from './inspect';
import { CACHE_TYPES, FILE_TYPES } from './quant';
import { drawReportCard } from './reportcard';
import {
  closeModal,
  hideGlossary,
  initGlossary,
  isModalOpen,
  renderApp,
  setModalOpenHook,
  showToast,
  type AppRefs,
} from './ui';
import {
  computeKvCache,
  evaluateFit,
  solveMaxContext,
  solveMaxLayers,
  summariseWeights,
  type FitResult,
  type KvSummary,
  type WeightSummary,
} from './vram';
import type { WorkerRequest, WorkerResponse } from './worker';

const root = document.querySelector<HTMLElement>('#app')!;
const refs: AppRefs = renderApp(root);

// ── Preferences ──────────────────────────────────────────────────────

const PREFS_KEY = 'ggufscope.prefs.v1';

interface Prefs {
  vram: number;
  vramCustom: number;
  ctx: number;
  cacheK: string;
  cacheV: string;
  nglAuto: boolean;
  ngl: number;
}

const DEFAULT_PREFS: Prefs = {
  vram: 24,
  vramCustom: 24,
  ctx: 8192,
  cacheK: 'f16',
  cacheV: 'f16',
  nglAuto: true,
  ngl: 99,
};

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function savePrefs(): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // A blocked localStorage costs a convenience, not the tool.
  }
}

const prefs = loadPrefs();

// ── State ────────────────────────────────────────────────────────────

interface Loaded {
  result: InspectResult;
  geo: Geometry;
  weights: WeightSummary;
  reconciliation: Reconciliation;
  /** Where the model came from, for the command line. */
  modelPath: string;
  hfRepo?: string;
  hfQuant?: string;
  chatTemplate?: string;
}

let loaded: Loaded | null = null;
let currentKv: KvSummary | null = null;
let currentFit: FitResult | null = null;
let activeTab = 'breakdown';
let requestSeq = 0;
let inFlight: { id: number; abort: () => void } | null = null;

// ── Worker ───────────────────────────────────────────────────────────

let worker: Worker | null = null;
try {
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
} catch {
  worker = null;
  log.system('Web Workers are unavailable — parsing on the main thread instead.', 'warn');
}

const pending = new Map<
  number,
  { resolve: (r: InspectResult) => void; reject: (e: Error) => void; onProgress: (p: InspectProgress) => void }
>();

if (worker) {
  worker.onmessage = (ev: MessageEvent<WorkerResponse>) => {
    const msg = ev.data;
    const entry = pending.get(msg.id);
    if (!entry) return;
    if (msg.kind === 'progress') entry.onProgress(msg.progress);
    else if (msg.kind === 'result') {
      pending.delete(msg.id);
      entry.resolve(msg.result);
    } else {
      pending.delete(msg.id);
      const err = new Error(msg.message);
      err.name = msg.name;
      entry.reject(err);
    }
  };
  worker.onerror = () => {
    log.system('The parser worker failed; falling back to the main thread.', 'warn');
    worker = null;
  };
}

function runInspect(
  id: number,
  target: InspectTarget,
  onProgress: (p: InspectProgress) => void,
): { promise: Promise<InspectResult>; abort: () => void } {
  if (worker) {
    const w = worker;
    const promise = new Promise<InspectResult>((resolve, reject) => {
      pending.set(id, { resolve, reject, onProgress });
      const req: WorkerRequest = { id, target };
      w.postMessage(req);
    });
    return {
      promise,
      abort: () => {
        const req: WorkerRequest = { id, cancel: true };
        w.postMessage(req);
        pending.delete(id);
      },
    };
  }
  const controller = new AbortController();
  return { promise: inspect(target, onProgress, controller.signal), abort: () => controller.abort() };
}

// ── Ingestion ────────────────────────────────────────────────────────

const EXAMPLES = [
  { label: 'Qwen3-8B-GGUF', value: 'Qwen/Qwen3-8B-GGUF' },
  { label: 'Llama-3.2-1B', value: 'bartowski/Llama-3.2-1B-Instruct-GGUF' },
  { label: 'gemma-3-4b-it', value: 'unsloth/gemma-3-4b-it-GGUF' },
  { label: 'Qwen3-32B', value: 'unsloth/Qwen3-32B-GGUF' },
];

function renderExamples(): void {
  for (const ex of EXAMPLES) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'chip';
    chip.textContent = ex.label;
    chip.addEventListener('click', () => {
      refs.addrInput.value = ex.value;
      void startFromAddress();
    });
    refs.examples.appendChild(chip);
  }
}

// ── Model search suggestions ─────────────────────────────────────────

/** Long enough that a whole model name costs two or three searches rather than
 *  one per keystroke — which matters because the repo listing shares the same
 *  rate-limit budget. Short enough that the list still feels attached to the
 *  keyboard. */
const SUGGEST_DEBOUNCE_MS = 250;

let suggestTimer: ReturnType<typeof setTimeout> | null = null;
let suggestAbort: AbortController | null = null;
let suggestIndex = -1;
let suggestWarned = false;

function hideSuggestions(): void {
  if (suggestTimer !== null) {
    clearTimeout(suggestTimer);
    suggestTimer = null;
  }
  suggestAbort?.abort();
  suggestAbort = null;
  suggestIndex = -1;
  refs.suggestList.hidden = true;
  refs.suggestList.innerHTML = '';
  refs.suggestNote.hidden = true;
  refs.suggestNote.textContent = '';
  refs.addrInput.setAttribute('aria-expanded', 'false');
  refs.addrInput.removeAttribute('aria-activedescendant');
}

/** A line where the list would be. Without it "searching", "nothing matched" and
 *  "the search switched itself off" all look identical — an empty space — and the
 *  Event log is not somewhere a phone user will ever look. Never a list row: the
 *  listbox holds options only, and this is nothing you can pick. */
function showSuggestNote(text: string): void {
  suggestIndex = -1;
  refs.suggestList.hidden = true;
  refs.suggestList.innerHTML = '';
  refs.addrInput.setAttribute('aria-expanded', 'false');
  refs.addrInput.removeAttribute('aria-activedescendant');
  refs.suggestNote.textContent = text;
  refs.suggestNote.hidden = false;
}

/** Names the refusal that actually happened rather than assuming a rate limit. */
function suggestOffText(): string {
  return `${suggestDisabledReason()} — suggestions are off for this session. Type or paste the full repo id.`;
}

function suggestRows(): HTMLButtonElement[] {
  return Array.from(refs.suggestList.querySelectorAll<HTMLButtonElement>('.suggest-row'));
}

function setSuggestActive(index: number): void {
  const rows = suggestRows();
  suggestIndex = index;
  rows.forEach((row, i) => {
    const on = i === index;
    row.classList.toggle('active', on);
    row.setAttribute('aria-selected', String(on));
  });
  const active = index >= 0 ? rows[index] : undefined;
  if (active) {
    refs.addrInput.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  } else {
    refs.addrInput.removeAttribute('aria-activedescendant');
  }
}

function renderSuggestions(hits: ModelHit[], query: string): void {
  if (!hits.length) {
    showSuggestNote(`No GGUF repos on Hugging Face match “${query}”.`);
    return;
  }
  refs.suggestNote.hidden = true;
  refs.suggestNote.textContent = '';
  refs.suggestList.innerHTML = '';
  suggestIndex = -1;
  refs.addrInput.removeAttribute('aria-activedescendant');
  hits.forEach((hit, i) => {
    const row = document.createElement('button');
    row.type = 'button';
    row.id = `suggest-opt-${i}`;
    row.className = 'suggest-row';
    row.setAttribute('role', 'option');
    row.setAttribute('aria-selected', 'false');
    row.innerHTML = `<span class="suggest-id">${escapeHtml(hit.id)}</span><span class="suggest-downloads">${hit.downloads.toLocaleString(
      'en',
    )} downloads</span>`;
    // preventDefault on pointerdown, and nothing else: it stops the press
    // blurring the input, which would otherwise take the list away before the
    // click landed. Choosing here would be wrong — on touch, pointerdown fires
    // the instant a finger lands, before the browser knows whether it is a tap
    // or the drag that scrolls this list. click is the event that knows.
    row.addEventListener('pointerdown', (e) => {
      e.preventDefault();
    });
    row.addEventListener('click', () => chooseSuggestion(hit.id));
    refs.suggestList.appendChild(row);
  });
  refs.suggestList.hidden = false;
  refs.addrInput.setAttribute('aria-expanded', 'true');
}

/** Hiding first also makes this idempotent, so a pointerdown that a browser
 *  still follows with a click cannot inspect the same repo twice. */
function chooseSuggestion(id: string): void {
  if (refs.suggestList.hidden) return;
  hideSuggestions();
  refs.addrInput.value = id;
  void startFromAddress();
}

async function runSuggest(query: string): Promise<void> {
  const controller = new AbortController();
  suggestAbort = controller;
  let hits: ModelHit[];
  try {
    hits = await searchGgufRepos(query, controller.signal);
  } catch {
    return; // aborted — a newer keystroke has already taken over
  }
  if (suggestAbort === controller) suggestAbort = null;
  if (isSuggestDisabled() && !suggestWarned) {
    suggestWarned = true;
    log.network(suggestOffText(), 'warn');
  }
  // A slow earlier response must not overwrite a list the field has moved on from.
  if (refs.addrInput.value.trim() !== query) return;
  if (isSuggestDisabled()) {
    showSuggestNote(suggestOffText());
    return;
  }
  renderSuggestions(hits, query);
}

function wireTypeahead(): void {
  refs.addrInput.addEventListener('input', () => {
    if (suggestTimer !== null) clearTimeout(suggestTimer);
    suggestTimer = null;
    suggestAbort?.abort();
    suggestAbort = null;
    const query = refs.addrInput.value.trim();
    if (!shouldSuggest(query)) {
      hideSuggestions();
      return;
    }
    if (isSuggestDisabled()) {
      showSuggestNote(suggestOffText());
      return;
    }
    if (navigator.onLine === false) {
      showSuggestNote('Offline — the model search needs a connection. Type or paste the full repo id.');
      return;
    }
    // Only when there is nothing to look at yet — swapping a live list out for
    // "searching" on every keystroke would just make it flicker.
    if (refs.suggestList.hidden) showSuggestNote('Searching Hugging Face…');
    suggestTimer = setTimeout(() => {
      suggestTimer = null;
      void runSuggest(query);
    }, SUGGEST_DEBOUNCE_MS);
  });
  // pointerdown+preventDefault keeps a tap from blurring, but tabbing away must
  // still put the list down.
  refs.addrInput.addEventListener('blur', () => hideSuggestions());
}

function setBusy(busy: boolean, message = ''): void {
  refs.inspectBtn.disabled = busy;
  refs.progressWrap.hidden = !busy;
  if (busy) {
    refs.progressText.textContent = message;
    refs.progressFill.style.width = '8%';
  }
}

function showError(err: unknown, retry?: () => void): void {
  const message = err instanceof Error ? err.message : String(err);
  if (message === 'aborted' || (err as Error)?.name === 'AbortError') return;

  refs.errorBox.hidden = false;
  refs.errorBox.innerHTML = `<strong>Could not read that model</strong>${escapeHtml(message)}`;
  if (retry) {
    const actions = document.createElement('div');
    actions.className = 'error-actions';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn-small';
    btn.textContent = 'Try again';
    btn.addEventListener('click', () => {
      refs.errorBox.hidden = true;
      retry();
    });
    actions.appendChild(btn);
    refs.errorBox.appendChild(actions);
  }
  log.system(message, 'err');
  setBusy(false);
}

function clearError(): void {
  refs.errorBox.hidden = true;
  refs.errorBox.textContent = '';
}

async function startFromAddress(): Promise<void> {
  clearError();
  refs.pickerPanel.hidden = true;
  hideSuggestions();
  const raw = refs.addrInput.value.trim();
  if (!raw) {
    refs.addrInput.focus();
    return;
  }

  let target;
  try {
    target = parseTarget(raw);
  } catch (err) {
    showError(err);
    return;
  }

  if (target.kind === 'file') {
    await loadRemote([target.url!], target.label, undefined, target.repo, quantFromFilename(target.label), target.label);
    return;
  }

  // A repo id: list its .gguf files so the user picks a quant from real sizes.
  setBusy(true, `Listing ${target.repo}…`);
  log.network(`Listing .gguf files in ${target.repo}`);
  try {
    const files = await listRepoGgufs(target.repo!, target.revision ?? 'main');
    const grouped = groupShards(files);
    log.network(`${grouped.length} model file${grouped.length === 1 ? '' : 's'} found`, 'ok');
    setBusy(false);

    // The `-hf repo:QUANT` shorthand carries a quant hint; honour it directly.
    const hint = target.path?.toLowerCase();
    const direct = hint ? grouped.find((f) => f.path.toLowerCase().includes(hint)) : undefined;
    if (direct) {
      await pickRepoFile(target.repo!, target.revision ?? 'main', direct, files);
      return;
    }
    if (grouped.length === 1) {
      await pickRepoFile(target.repo!, target.revision ?? 'main', grouped[0], files);
      return;
    }
    renderPicker(target.repo!, target.revision ?? 'main', grouped, files);
  } catch (err) {
    showError(err, () => void startFromAddress());
  }
}

function renderPicker(
  repo: string,
  revision: string,
  grouped: Array<RepoFile & { totalSize: number; shardCount: number }>,
  all: RepoFile[],
): void {
  refs.pickerTitle.textContent = `Pick a quantisation — ${repo}`;
  refs.pickerList.innerHTML = '';
  for (const f of grouped) {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'file-row';
    const shard = f.shardCount > 1 ? `<span class="file-shards">${f.shardCount} shards</span>` : '<span></span>';
    row.innerHTML = `<span class="file-name">${escapeHtml(f.path)}</span><span class="file-size">${formatBytes(
      f.totalSize,
    )}</span>${shard}`;
    row.addEventListener('click', () => void pickRepoFile(repo, revision, f, all));
    refs.pickerList.appendChild(row);
  }
  refs.pickerPanel.hidden = false;
  refs.pickerPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

async function pickRepoFile(
  repo: string,
  revision: string,
  file: RepoFile & { totalSize?: number; shardCount?: number },
  all: RepoFile[],
): Promise<void> {
  refs.pickerPanel.hidden = true;
  const urls: string[] = [];
  if (file.shard && (file.shardCount ?? 1) > 1) {
    const siblings = all
      .filter((f) => f.shard && f.shard.base === file.shard!.base)
      .sort((a, b) => a.shard!.index - b.shard!.index);
    for (const s of siblings) urls.push(hfResolveUrl(repo, revision, s.path));
  } else {
    urls.push(hfResolveUrl(repo, revision, file.path));
  }
  const name = file.path.split('/').pop()!;
  await loadRemote(urls, name, file.totalSize, repo, quantFromFilename(name), `./${name}`);
}

function quantFromFilename(name: string): string | undefined {
  const m = /[-.]((?:IQ|Q)\d+(?:_[A-Z0-9]+)*|BF16|F16|F32|MXFP4)[-.]/i.exec(`-${name}`.replace(/\.gguf$/i, '.'));
  return m ? m[1].toUpperCase() : undefined;
}

async function loadRemote(
  urls: string[],
  label: string,
  knownTotalBytes: number | undefined,
  hfRepo: string | undefined,
  hfQuant: string | undefined,
  modelPath: string,
): Promise<void> {
  await runLoad({ kind: 'remote', urls, label, knownTotalBytes }, { hfRepo, hfQuant, modelPath });
}

async function loadLocal(file: File): Promise<void> {
  if (!/\.gguf$/i.test(file.name)) {
    showError(new NotGguf(`"${file.name}" is not a .gguf file. GGUFScope reads GGUF models only.`));
    return;
  }
  log.system(`Reading local file ${file.name} (${formatBytes(file.size)}) — nothing is uploaded`);
  await runLoad({ kind: 'local', file, label: file.name }, { modelPath: `./${file.name}` });
}

async function runLoad(
  target: InspectTarget,
  meta: { hfRepo?: string; hfQuant?: string; modelPath: string },
): Promise<void> {
  clearError();
  inFlight?.abort();

  const id = ++requestSeq;
  setBusy(true, 'Starting…');
  log.network(`Inspecting ${target.label}`);

  const { promise, abort } = runInspect(id, target, (p) => {
    if (id !== requestSeq) return;
    refs.progressText.textContent = p.message;
    if (p.totalBytes && p.bytesRead) {
      const pct = Math.min(100, Math.max(8, (p.bytesRead / Math.min(p.totalBytes, 8 << 20)) * 100));
      refs.progressFill.style.width = `${pct}%`;
    }
    if (p.phase === 'parsing') log.parse(p.message);
  });
  inFlight = { id, abort };

  try {
    const result = await promise;
    if (id !== requestSeq) return;
    inFlight = null;
    refs.progressFill.style.width = '100%';
    onLoaded(result, meta);
  } catch (err) {
    if (id !== requestSeq) return;
    inFlight = null;
    showError(err, () => void runLoad(target, meta));
  }
}

function onLoaded(result: InspectResult, meta: { hfRepo?: string; hfQuant?: string; modelPath: string }): void {
  const { parsed } = result;
  log.parse(
    `Parsed ${parsed.tensors.length} tensors and ${parsed.kv.size} metadata keys from ${formatBytes(
      result.bytesRead,
    )}${result.totalBytes ? ` of ${formatBytes(result.totalBytes)}` : ''}`,
    'ok',
  );

  let geo: Geometry;
  try {
    geo = resolveGeometry(parsed.kv);
  } catch (err) {
    showError(err instanceof GeometryError ? err : new Error(String(err)));
    return;
  }

  const weights = summariseWeights(parsed.tensors, parsed.dataStart, parsed.alignment, geo.nLayer);
  const reconciliation = reconcile(weights.expectedFileBytes, result.totalBytes, parsed.dataStart);

  log.compute(
    `${formatParams(weights.params)} parameters, ${bytesToGiB(weights.totalBytes)} of weights at ${formatBpw(weights.bpw)}`,
    'ok',
  );
  log.compute(
    reconciliation.status === 'verified'
      ? 'Reconciled: the tensor table sums to exactly the file size (delta 0)'
      : `Reconciliation: ${reconciliation.status} (delta ${reconciliation.delta ?? 'unknown'})`,
    reconciliation.status === 'verified' || reconciliation.status === 'header-only' ? 'ok' : 'warn',
  );
  for (const a of geo.assumptions) log.compute(a, 'warn');
  for (const cn of geo.cautions) log.compute(cn, 'warn');

  loaded = {
    result,
    geo,
    weights,
    reconciliation,
    modelPath: meta.modelPath,
    hfRepo: meta.hfRepo,
    hfQuant: meta.hfQuant,
    chatTemplate: metaString(parsed.kv, 'tokenizer.chat_template'),
  };

  // Default the context to something the model actually supports.
  if (geo.ctxTrain && prefs.ctx > geo.ctxTrain) prefs.ctx = Math.min(8192, geo.ctxTrain);
  buildContextOptions(geo);

  setBusy(false);
  refs.configPanel.hidden = false;
  recompute();
  refs.verdictPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// ── Config ───────────────────────────────────────────────────────────

const VRAM_PRESETS = [
  { label: '8 GiB — RTX 3050 / 4060', value: 8 },
  { label: '10 GiB — RTX 3080', value: 10 },
  { label: '12 GiB — RTX 3060 / 4070', value: 12 },
  { label: '16 GiB — RTX 4060 Ti / 5060 Ti', value: 16 },
  { label: '24 GiB — RTX 3090 / 4090', value: 24 },
  { label: '32 GiB — RTX 5090', value: 32 },
  { label: '48 GiB — RTX 6000 Ada', value: 48 },
  { label: '80 GiB — A100 / H100', value: 80 },
  { label: 'Custom…', value: -1 },
];

const CTX_PRESETS = [512, 1024, 2048, 4096, 8192, 16384, 32768, 65536, 131072, 262144];

function buildControls(): void {
  for (const p of VRAM_PRESETS) {
    const o = document.createElement('option');
    o.value = String(p.value);
    o.textContent = p.label;
    refs.vramSelect.appendChild(o);
  }
  refs.vramSelect.value = String(prefs.vram);
  refs.vramCustom.value = String(prefs.vramCustom);
  document.querySelector<HTMLElement>('#vram-custom-field')!.hidden = prefs.vram !== -1;

  for (const sel of [refs.cacheKSelect, refs.cacheVSelect]) {
    for (const ct of CACHE_TYPES) {
      const o = document.createElement('option');
      o.value = ct.flag;
      o.textContent = ct.label;
      sel.appendChild(o);
    }
  }
  refs.cacheKSelect.value = prefs.cacheK;
  refs.cacheVSelect.value = prefs.cacheV;
  refs.nglAuto.checked = prefs.nglAuto;
  refs.nglInput.value = String(prefs.ngl);
  refs.nglInput.disabled = prefs.nglAuto;
}

function buildContextOptions(geo: Geometry): void {
  const max = geo.ctxTrain ?? 131072;
  const values = CTX_PRESETS.filter((v) => v <= max);
  if (geo.ctxTrain && !values.includes(geo.ctxTrain)) values.push(geo.ctxTrain);
  values.sort((a, b) => a - b);

  refs.ctxSelect.innerHTML = '';
  for (const v of values) {
    const o = document.createElement('option');
    o.value = String(v);
    o.textContent = v === geo.ctxTrain ? `${formatContext(v)} — model max` : formatContext(v);
    refs.ctxSelect.appendChild(o);
  }
  if (!values.includes(prefs.ctx)) prefs.ctx = values[values.length - 1] ?? 4096;
  refs.ctxSelect.value = String(prefs.ctx);
}

function vramBytes(): number {
  return gibToBytes(prefs.vram === -1 ? prefs.vramCustom : prefs.vram);
}

function readControls(): void {
  prefs.vram = Number(refs.vramSelect.value);
  prefs.vramCustom = Number(refs.vramCustom.value) || DEFAULT_PREFS.vramCustom;
  prefs.ctx = Number(refs.ctxSelect.value) || DEFAULT_PREFS.ctx;
  prefs.cacheK = refs.cacheKSelect.value;
  prefs.cacheV = refs.cacheVSelect.value;
  prefs.nglAuto = refs.nglAuto.checked;
  if (!prefs.nglAuto) prefs.ngl = Math.max(0, Number(refs.nglInput.value) || 0);
  document.querySelector<HTMLElement>('#vram-custom-field')!.hidden = prefs.vram !== -1;
  refs.nglInput.disabled = prefs.nglAuto;
  savePrefs();
}

// ── The calculation + render ─────────────────────────────────────────

function recompute(): void {
  if (!loaded) return;
  const { geo, weights } = loaded;

  const kv = computeKvCache(geo, { nCtx: prefs.ctx, cacheTypeK: prefs.cacheK, cacheTypeV: prefs.cacheV });
  const vram = vramBytes();

  let ngl = prefs.ngl;
  if (prefs.nglAuto) {
    ngl = solveMaxLayers(weights, kv, geo, vram);
    refs.nglInput.value = String(ngl);
  }
  const fit = evaluateFit(weights, kv, geo, ngl, vram);

  currentKv = kv;
  currentFit = fit;

  renderVerdict(loaded, kv, fit, ngl);
  renderBreakdown(loaded, kv, fit);
  renderQuantMix(loaded);
  renderCommand(loaded, fit, ngl);
  renderMetadata(loaded);
  renderTemplate(loaded);
  drawReportCard(refs.reportCanvas, {
    label: loaded.result.label,
    geo,
    weights,
    kv,
    fit,
    nCtx: prefs.ctx,
    cacheTypeK: prefs.cacheK,
    cacheTypeV: prefs.cacheV,
    verified: loaded.reconciliation.status === 'verified',
  });

  refs.verdictPanel.hidden = false;
  refs.sharePngBtn.hidden = typeof navigator.canShare !== 'function';
}

const VERDICT_WORD: Record<FitResult['verdict'], string> = {
  fits: 'Fits',
  tight: 'Just fits',
  no: "Doesn't fit",
};

function renderVerdict(l: Loaded, kv: KvSummary, fit: FitResult, ngl: number): void {
  const { geo, weights, result } = l;

  refs.verdictPanel.dataset.verdict = fit.verdict;
  refs.modelName.textContent = result.label;

  const bits = [
    geo.arch,
    `${formatParams(weights.params)} params`,
    formatBpw(weights.bpw),
    `${geo.nLayer} layers`,
    `${geo.nHead}Q/${geo.nHeadKv[0]}KV heads`,
  ];
  const ft = metaNumber(result.parsed.kv, 'general.file_type');
  if (ft !== undefined && FILE_TYPES[ft]) bits.splice(1, 0, FILE_TYPES[ft]);
  refs.modelSub.textContent = bits.join('  ·  ');

  const read = result.local
    ? `read ${formatBytes(result.bytesRead)} locally`
    : `read ${formatBytes(result.bytesRead)}${
        result.totalBytes ? ` of ${formatBytes(result.totalBytes)} — ${formatPercent(result.bytesRead / result.totalBytes, 2)}` : ''
      }`;
  refs.readBadge.textContent = read;

  refs.verdictWord.textContent = VERDICT_WORD[fit.verdict];
  refs.verdictDetail.textContent =
    fit.headroomBytes >= 0
      ? `on ${bytesToGiB(fit.vramBytes, 0)} — ${bytesToGiB(fit.headroomBytes, 2)} to spare`
      : `on ${bytesToGiB(fit.vramBytes, 0)} — over by ${bytesToGiB(-fit.headroomBytes, 2)}`;

  renderMemBar(fit);
  refs.verdictAdvice.innerHTML = buildAdvice(l, kv, fit, ngl);
  initGlossary(refs.verdictAdvice);
}

function renderMemBar(fit: FitResult): void {
  const cap = Math.max(fit.vramBytes, fit.requiredBytes);
  const segs: Array<[string, number, string]> = [
    ['weights', fit.weightBytesGpu, 'var(--signal)'],
    ['kv', fit.kvBytesGpu, '#4fb0ff'],
    ['overhead', fit.reserveBytes, '#6b6b8f'],
  ];

  refs.memBar.innerHTML = '';
  for (const [name, bytes] of segs) {
    const el = document.createElement('div');
    el.className = 'membar-seg';
    el.dataset.seg = name;
    el.style.width = `${(bytes / cap) * 100}%`;
    refs.memBar.appendChild(el);
  }
  if (fit.vramBytes < cap) {
    const limit = document.createElement('div');
    limit.className = 'membar-limit';
    limit.style.left = `${(fit.vramBytes / cap) * 100}%`;
    limit.title = 'Your GPU memory';
    refs.memBar.appendChild(limit);
  }

  refs.memLegend.innerHTML = '';
  const labels: Array<[string, string, number]> = [
    ['weights', 'var(--signal)', fit.weightBytesGpu],
    [`KV cache`, '#4fb0ff', fit.kvBytesGpu],
    ['working memory', '#6b6b8f', fit.reserveBytes],
  ];
  for (const [name, colour, bytes] of labels) {
    const key = document.createElement('span');
    key.className = 'legend-key';
    key.innerHTML = `<span class="legend-dot" style="background:${colour}"></span>${name} ${bytesToGiB(bytes, 2)}`;
    refs.memLegend.appendChild(key);
  }
}

function buildAdvice(l: Loaded, kv: KvSummary, fit: FitResult, ngl: number): string {
  const { geo, weights } = l;
  const parts: string[] = [];
  const vram = fit.vramBytes;

  if (fit.verdict === 'no') {
    // Offer the cheapest real remedies, in the order they cost the user least.
    const cheaper = solveMaxContext(weights, geo, prefs.cacheK, prefs.cacheV, vram);
    if (cheaper >= 512) {
      parts.push(
        `Every layer fits at <strong>${formatContext(cheaper)}</strong> of <span data-term="context length">context</span> instead of ${formatContext(kv.paddedCtx)}.`,
      );
    }
    if (prefs.cacheK === 'f16' || prefs.cacheV === 'f16') {
      const q8 = solveMaxContext(weights, geo, 'q8_0', 'q8_0', vram);
      if (q8 > kv.paddedCtx) {
        parts.push(
          `With a <span data-term="cache type">q8_0 KV cache</span> you could run <strong>${formatContext(q8)}</strong> — about half the cache for a small quality cost.`,
        );
      } else if (q8 >= 512 && q8 > cheaper) {
        parts.push(`A q8_0 KV cache would get you to <strong>${formatContext(q8)}</strong>.`);
      }
    }
    const maxNgl = solveMaxLayers(weights, kv, geo, vram);
    if (maxNgl > 0 && maxNgl <= geo.nLayer) {
      // "Otherwise" only reads correctly when something was suggested first.
      const lead = parts.length ? 'Otherwise ' : '';
      parts.push(
        `${lead}<code>-ngl ${maxNgl}</code> keeps ${maxNgl} of ${geo.nLayer} layers on the GPU, with the other ${
          geo.nLayer - maxNgl
        } on the CPU — it will run, slowly.`,
      );
    } else if (maxNgl === 0) {
      parts.push('Nothing fits on this card at these settings — a smaller quantisation is the only route.');
    }
  } else {
    // A "fits" reached by leaving layers on the CPU is not the same as a
    // "fits" with everything offloaded, and the difference is most of the
    // speed — so say so before quoting spare context.
    if (fit.plan.layersOnGpu < geo.nLayer) {
      parts.push(
        `Only <strong>${fit.plan.layersOnGpu} of ${geo.nLayer}</strong> layers fit, so the remaining ${
          geo.nLayer - fit.plan.layersOnGpu
        } run on the CPU — expect a large slowdown. A smaller quantisation or a shorter context is usually the better trade.`,
      );
    }
    const maxCtx = solveMaxContext(weights, geo, prefs.cacheK, prefs.cacheV, vram);
    if (maxCtx > kv.paddedCtx && fit.plan.layersOnGpu >= geo.nLayer) {
      parts.push(`You have room for up to <strong>${formatContext(maxCtx)}</strong> of context at these settings.`);
    }
    if (fit.verdict === 'tight') {
      parts.push(
        'That is under 4% headroom, which is close enough that a desktop compositor or a second process could tip it over.',
      );
    }
    if (ngl > geo.nLayer) parts.push('Every layer plus the output head sits on the GPU.');
  }

  for (const caution of geo.cautions) parts.push(`<strong>Note:</strong> ${escapeHtml(caution)}`);

  return parts.join(' ');
}

function row(cells: string[], cls = ''): string {
  return `<tr class="${cls}">${cells.map((c, i) => `<td${i > 0 ? ' class="num"' : ''}>${c}</td>`).join('')}</tr>`;
}

function renderBreakdown(l: Loaded, kv: KvSummary, fit: FitResult): void {
  const { geo, weights, reconciliation, result } = l;
  const rows: string[] = [];

  rows.push(
    `<tr><th>Line item</th><th class="num">On GPU</th><th class="num">Total</th></tr>`,
  );
  rows.push(
    row([
      `Weights — ${fit.plan.layersOnGpu} of ${geo.nLayer} layers${fit.plan.outputOnGpu ? ' + output head' : ''}`,
      bytesToGiB(fit.weightBytesGpu),
      bytesToGiB(weights.totalBytes),
    ]),
  );
  rows.push(
    row([
      `<span data-term="KV cache">KV cache</span> @ ${formatContext(kv.paddedCtx)} (${prefs.cacheK}/${prefs.cacheV})`,
      bytesToGiB(fit.kvBytesGpu),
      bytesToGiB(kv.totalBytes),
    ]),
  );
  rows.push(
    row(
      [
        `<span data-term="compute buffer">Working memory</span> — allowance, not a computed figure`,
        bytesToGiB(fit.reserveBytes),
        '—',
      ],
      'sub',
    ),
  );
  rows.push(row(['Required in VRAM', bytesToGiB(fit.requiredBytes), ''], 'total'));
  rows.push(
    row([
      'Your GPU memory',
      bytesToGiB(fit.vramBytes, 0),
      fit.headroomBytes >= 0 ? `${bytesToGiB(fit.headroomBytes)} free` : `${bytesToGiB(-fit.headroomBytes)} short`,
    ]),
  );

  rows.push(`<tr><th>Detail</th><th class="num"></th><th class="num"></th></tr>`);
  rows.push(row(['KV cache per token', `${formatBytes(kv.bytesPerToken)}`, '']));
  if (kv.paddedCtx !== prefs.ctx) {
    rows.push(
      row(
        [`Context rounded up to a multiple of 256, as llama.cpp does`, `${formatContext(prefs.ctx)} → ${formatContext(kv.paddedCtx)}`, ''],
        'sub',
      ),
    );
  }
  rows.push(row(['Parameters', formatParams(weights.params), '']));
  rows.push(row(['<span data-term="bits per weight">Bits per weight</span> (real)', formatBpw(weights.bpw), '']));
  if (result.totalBytes) rows.push(row(['File size', formatBytes(result.totalBytes), '']));
  if (result.shards?.length) {
    rows.push(row([`Split across ${result.shards.length} shards, all read`, '', ''], 'sub'));
  }

  const badge =
    reconciliation.status === 'verified'
      ? `<span style="color:var(--good)">✓ verified</span>`
      : reconciliation.status === 'header-only'
        ? `<span style="color:var(--text-tertiary)">header-only fixture</span>`
        : `<span class="warn-cell">⚠ ${reconciliation.status}</span>`;
  rows.push(row([`Tensor table vs file size`, badge, '']));
  rows.push(row([`<span class="${reconciliation.status === 'verified' ? '' : 'warn-cell'}">${escapeHtml(reconciliation.message)}</span>`, '', ''], 'sub'));

  if (weights.unsizeable.length) {
    rows.push(
      row(
        [
          `<span class="warn-cell">${weights.unsizeable.length} tensor(s) could not be sized: ${escapeHtml(
            weights.unsizeable[0].detail,
          )}</span>`,
          '',
          '',
        ],
        'sub',
      ),
    );
  }
  for (const a of geo.assumptions) rows.push(row([`<span class="warn-cell">Assumption: ${escapeHtml(a)}</span>`, '', ''], 'sub'));

  refs.breakdownBody.innerHTML = rows.join('');
  initGlossary(refs.breakdownBody);
}

function renderQuantMix(l: Loaded): void {
  const { weights } = l;
  const rows = [`<tr><th>Type</th><th class="num">Tensors</th><th class="num">Size</th><th class="num">bpw</th><th class="num">Share</th></tr>`];
  for (const t of weights.byType) {
    rows.push(
      row([
        `<span class="type-pill">${t.name}</span>`,
        String(t.count),
        // Not GiB: the F32 norm tensors are a few megabytes and would all read
        // "0.00 GiB", which looks like a bug rather than a small number.
        formatBytes(t.bytes),
        t.bitsPerWeight.toFixed(3),
        formatPercent(t.bytes / weights.totalBytes, 1),
      ]),
    );
  }
  rows.push(row(['Total', String(weights.byType.reduce((a, b) => a + b.count, 0)), formatBytes(weights.totalBytes), formatBpw(weights.bpw), '100%'], 'total'));
  refs.quantBody.innerHTML = rows.join('');
}

function renderCommand(l: Loaded, fit: FitResult, ngl: number): void {
  const cmd = buildLlamaServerCommand({
    modelPath: l.modelPath,
    hfRepo: l.hfRepo,
    hfQuant: l.hfQuant,
    nCtx: prefs.ctx,
    ngl,
    nLayer: l.geo.nLayer,
    cacheTypeK: prefs.cacheK,
    cacheTypeV: prefs.cacheV,
  });
  refs.cmdBlock.innerHTML = highlightCommand(cmd.command);
  refs.cmdBlock.dataset.raw = cmd.command;

  const mf = buildModelfile({
    modelPath: l.modelPath,
    nCtx: prefs.ctx,
    ngl,
    nLayer: l.geo.nLayer,
    hasChatTemplate: Boolean(l.chatTemplate),
  });
  refs.ollamaBlock.textContent = mf.text;
  refs.ollamaBlock.dataset.raw = mf.text;

  const note = document.querySelector<HTMLElement>('#ollama-note')!;
  note.innerHTML = [...cmd.notes, ...mf.notes].map((n) => `<span>${escapeHtml(n)}</span>`).join('<br><br>');
  if (fit.verdict === 'no') {
    note.innerHTML = `<span class="warn-cell">These flags are for the settings above, which do not currently fit — adjust the context or layers first.</span><br><br>${note.innerHTML}`;
  }
}

function highlightCommand(cmd: string): string {
  return escapeHtml(cmd).replace(/(^|\s)(-{1,2}[a-z][\w-]*)/g, '$1<span class="flag">$2</span>');
}

function renderMetadata(l: Loaded): void {
  const kv = l.result.parsed.kv;
  const interesting = [
    'general.architecture',
    'general.name',
    'general.basename',
    'general.size_label',
    'general.license',
    'general.quantization_version',
    'general.file_type',
    'general.alignment',
    `${l.geo.arch}.block_count`,
    `${l.geo.arch}.context_length`,
    `${l.geo.arch}.embedding_length`,
    `${l.geo.arch}.feed_forward_length`,
    `${l.geo.arch}.attention.head_count`,
    `${l.geo.arch}.attention.head_count_kv`,
    `${l.geo.arch}.attention.key_length`,
    `${l.geo.arch}.attention.value_length`,
    `${l.geo.arch}.attention.sliding_window`,
    `${l.geo.arch}.expert_count`,
    `${l.geo.arch}.expert_used_count`,
    `${l.geo.arch}.rope.freq_base`,
    `${l.geo.arch}.vocab_size`,
    'tokenizer.ggml.model',
    'tokenizer.ggml.pre',
    'tokenizer.ggml.bos_token_id',
    'tokenizer.ggml.eos_token_id',
  ];

  const rows = [`<tr><th>Key</th><th class="num">Value</th></tr>`];
  for (const key of interesting) {
    const v = kv.get(key);
    if (!v) continue;
    rows.push(row([escapeHtml(key), escapeHtml(describeValue(v))]));
  }

  const shown = new Set(interesting);
  const others = [...kv.keys()].filter((k) => !shown.has(k) && k !== 'tokenizer.chat_template');
  if (others.length) {
    rows.push(`<tr><th>Other keys (${others.length})</th><th class="num"></th></tr>`);
    for (const key of others) {
      rows.push(row([escapeHtml(key), escapeHtml(describeValue(kv.get(key)!))], 'sub'));
    }
  }
  refs.metaBody.innerHTML = rows.join('');
}

function describeValue(v: GgufValue): string {
  if (v.kind === 'string') return v.value.length > 90 ? `${v.value.slice(0, 90)}…` : v.value;
  if (v.kind === 'scalar') return String(v.value);
  if (v.values) return `[${v.values.slice(0, 8).join(', ')}${v.count > 8 ? ', …' : ''}]`;
  return `${v.count.toLocaleString('en')} entries (not loaded)`;
}

const PREVIEW_MESSAGES = [
  { role: 'system', content: 'You are a helpful assistant.' },
  { role: 'user', content: 'What is the capital of Australia?' },
  { role: 'assistant', content: 'Canberra.' },
  { role: 'user', content: 'And its population?' },
];

function renderTemplate(l: Loaded): void {
  const body = refs.templateBody;
  const src = l.chatTemplate;
  if (!src) {
    body.innerHTML = `<p class="artefact-note">This file carries no <code>tokenizer.chat_template</code>. llama.cpp will fall back to a built-in template guessed from the architecture, which is a common reason a model replies oddly.</p>`;
    refs.dlTemplateBtn.disabled = true;
    return;
  }
  refs.dlTemplateBtn.disabled = false;

  let rendered: string | null = null;
  let error: string | null = null;
  try {
    const tpl = new Template(src);
    rendered = tpl.render({
      messages: PREVIEW_MESSAGES,
      add_generation_prompt: true,
      bos_token: l.result.specialTokens.bos ?? '',
      eos_token: l.result.specialTokens.eos ?? '',
      unk_token: l.result.specialTokens.unk ?? '',
      pad_token: l.result.specialTokens.pad ?? '',
    });
  } catch (err) {
    error = err instanceof Error ? err.message : String(err);
  }

  const preview = rendered
    ? `<div class="artefact-head"><span class="artefact-label">rendered with four example messages</span></div>
       <pre class="codeblock template-box">${escapeHtml(rendered)}</pre>`
    : `<div class="artefact-head"><span class="artefact-label warn-cell">could not render</span></div>
       <p class="artefact-note warn-cell">${escapeHtml(error ?? 'unknown error')}<br>
       Some templates call helpers that only exist inside a Python tokenizer (<code>strftime_now</code>,
       <code>raise_exception</code>), so failing to render here does not mean the model is broken — the
       source below is exactly what the file contains.</p>`;

  body.innerHTML = `${preview}
    <div class="artefact-head"><span class="artefact-label">template source</span></div>
    <pre class="codeblock template-box">${escapeHtml(src)}</pre>`;
}

// ── Artefact delivery ────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  log.export(`Saved ${filename}`, 'ok');
}

async function saveWithPicker(blob: Blob, filename: string, description: string, accept: Record<string, string[]>): Promise<void> {
  const picker = (window as unknown as { showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle> })
    .showSaveFilePicker;
  if (typeof picker === 'function') {
    try {
      const handle = await picker({ suggestedName: filename, types: [{ description, accept }] });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      log.export(`Saved ${filename}`, 'ok');
      showToast('Saved');
      return;
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      // Any other failure falls through to the anchor download.
    }
  }
  downloadBlob(blob, filename);
  showToast('Saved');
}

async function copyText(text: string, what: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    showToast(`${what} copied`);
    log.export(`${what} copied to clipboard`, 'ok');
  } catch {
    showToast('Clipboard blocked — select and copy manually');
  }
}

function reportBasename(): string {
  return slugify(loaded?.result.label ?? 'model');
}

function currentReportJson(): string {
  if (!loaded || !currentKv || !currentFit) return '{}';
  const ft = metaNumber(loaded.result.parsed.kv, 'general.file_type');
  return buildReportJson({
    label: loaded.result.label,
    source: loaded.result.local ? 'local file' : (loaded.hfRepo ?? 'url'),
    geo: loaded.geo,
    weights: loaded.weights,
    kv: currentKv,
    fit: currentFit,
    fileBytes: loaded.result.totalBytes,
    bytesRead: loaded.result.bytesRead,
    nCtx: prefs.ctx,
    cacheTypeK: prefs.cacheK,
    cacheTypeV: prefs.cacheV,
    ngl: currentFit.plan.layersOnGpu + (currentFit.plan.outputOnGpu ? 1 : 0),
    fileType: ft !== undefined ? FILE_TYPES[ft] : undefined,
    reconciliation: loaded.reconciliation,
    generatedAt: new Date().toISOString(),
  });
}

function canvasBlob(): Promise<Blob | null> {
  return new Promise((resolve) => refs.reportCanvas.toBlob(resolve, 'image/png'));
}

// ── Wiring ───────────────────────────────────────────────────────────

function switchTab(name: string): void {
  activeTab = name;
  for (const [key, panel] of Object.entries(refs.tabPanels)) panel.hidden = key !== name;
  refs.tabs.querySelectorAll<HTMLButtonElement>('.tab').forEach((b) => {
    const on = b.dataset.tab === name;
    b.classList.toggle('active', on);
    b.setAttribute('aria-selected', String(on));
  });
}

function wire(): void {
  renderExamples();
  wireTypeahead();
  buildControls();

  refs.inspectBtn.addEventListener('click', () => void startFromAddress());
  refs.addrInput.addEventListener('keydown', (e) => {
    const rows = suggestRows();
    if (rows.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      // preventDefault so the caret stops jumping to either end of the field.
      e.preventDefault();
      let next = suggestIndex + (e.key === 'ArrowDown' ? 1 : -1);
      if (next < 0) next = rows.length - 1;
      if (next >= rows.length) next = 0;
      setSuggestActive(next);
      return;
    }
    if (rows.length && e.key === 'Escape') {
      // The list is the innermost thing open; don't let Escape close the drawer too.
      e.stopPropagation();
      hideSuggestions();
      return;
    }
    if (e.key === 'Enter') {
      const active = suggestIndex >= 0 ? rows[suggestIndex] : undefined;
      if (active) {
        active.click();
        return;
      }
      void startFromAddress();
    }
  });

  // Drop zone
  refs.dropZone.addEventListener('click', () => refs.fileInput.click());
  refs.dropZone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      refs.fileInput.click();
    }
  });
  refs.fileInput.addEventListener('change', () => {
    const f = refs.fileInput.files?.[0];
    if (f) void loadLocal(f);
    refs.fileInput.value = '';
  });
  for (const type of ['dragenter', 'dragover']) {
    refs.dropZone.addEventListener(type, (e) => {
      e.preventDefault();
      refs.dropZone.classList.add('drag');
    });
  }
  for (const type of ['dragleave', 'drop']) {
    refs.dropZone.addEventListener(type, () => refs.dropZone.classList.remove('drag'));
  }
  refs.dropZone.addEventListener('drop', (e) => {
    e.preventDefault();
    const f = (e as DragEvent).dataTransfer?.files?.[0];
    if (f) void loadLocal(f);
  });

  // The sample sits inside the drop zone, so its click must not also open the
  // file picker.
  refs.sampleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    void loadSample();
  });

  // Config
  for (const el of [refs.vramSelect, refs.ctxSelect, refs.cacheKSelect, refs.cacheVSelect]) {
    el.addEventListener('change', () => {
      readControls();
      recompute();
    });
  }
  for (const el of [refs.vramCustom, refs.nglInput]) {
    el.addEventListener('input', () => {
      readControls();
      recompute();
    });
  }
  refs.nglAuto.addEventListener('change', () => {
    readControls();
    recompute();
  });

  // Tabs
  refs.tabs.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.tab');
    if (btn?.dataset.tab) switchTab(btn.dataset.tab);
  });

  // Artefacts
  refs.copyCmdBtn.addEventListener('click', () => void copyText(refs.cmdBlock.dataset.raw ?? '', 'Command'));
  refs.copyOllamaBtn.addEventListener('click', () => void copyText(refs.ollamaBlock.dataset.raw ?? '', 'Modelfile'));
  refs.dlJsonBtn.addEventListener('click', () => {
    const blob = new Blob([currentReportJson()], { type: 'application/json' });
    void saveWithPicker(blob, `${reportBasename()}-fit-report.json`, 'JSON report', { 'application/json': ['.json'] });
  });
  refs.dlPngBtn.addEventListener('click', async () => {
    const blob = await canvasBlob();
    if (blob) await saveWithPicker(blob, `${reportBasename()}-fit.png`, 'PNG image', { 'image/png': ['.png'] });
  });
  refs.dlTemplateBtn.addEventListener('click', () => {
    if (!loaded?.chatTemplate) return;
    const blob = new Blob([loaded.chatTemplate], { type: 'text/plain' });
    void saveWithPicker(blob, `${reportBasename()}-chat_template.jinja`, 'Jinja template', { 'text/plain': ['.jinja'] });
  });
  refs.sharePngBtn.addEventListener('click', async () => {
    const blob = await canvasBlob();
    if (!blob) return;
    const file = new File([blob], `${reportBasename()}-fit.png`, { type: 'image/png' });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: 'GGUFScope fit report' });
        log.export('Shared the report card', 'ok');
      } catch {
        // A cancelled share is not an error.
      }
    } else {
      await saveWithPicker(blob, file.name, 'PNG image', { 'image/png': ['.png'] });
    }
  });

  // Drawer
  const detach = { fn: null as null | (() => void) };
  const setDrawer = (open: boolean) => {
    refs.drawer.hidden = !open;
    refs.drawer.setAttribute('aria-hidden', String(!open));
    refs.drawerToggle.setAttribute('aria-expanded', String(open));
    if (open) {
      refs.scrim.hidden = false;
      detach.fn = mountEventDrawer(refs.drawerBody, () => setDrawer(false));
    } else {
      detach.fn?.();
      detach.fn = null;
      if (!isModalOpen(root)) refs.scrim.hidden = true;
    }
  };
  refs.drawerToggle.addEventListener('click', () => setDrawer(refs.drawer.hidden));
  refs.scrim.addEventListener('click', () => {
    if (!refs.drawer.hidden) setDrawer(false);
    if (isModalOpen(root)) closeModal(root);
  });

  // Keyboard
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      hideGlossary();
      if (isModalOpen(root)) closeModal(root);
      else if (!refs.drawer.hidden) setDrawer(false);
    }
  });
  document.addEventListener('paste', (e) => {
    if (document.activeElement === refs.addrInput) return;
    const text = e.clipboardData?.getData('text')?.trim();
    if (!text || isModalOpen(root)) return;
    if (/gguf|huggingface\.co|hf\.co|^[\w.-]+\/[\w.-]+$/i.test(text)) {
      refs.addrInput.value = text;
      void startFromAddress();
    }
  });

  setModalOpenHook((id) => log.system(`Opened the ${id} panel`));
  window.addEventListener('beforeunload', () => inFlight?.abort());
}

async function loadSample(): Promise<void> {
  clearError();
  try {
    log.system('Loading the bundled sample header');
    const res = await fetch('samples/demo-8b-q4_k_m.header.gguf');
    if (!res.ok) throw new Error(`sample unavailable (${res.status})`);
    const blob = await res.blob();
    const file = new File([blob], 'demo-8b-q4_k_m.header.gguf', { type: 'application/octet-stream' });
    // Deliberately the same path a dropped file takes — one ingestion route.
    await loadLocal(file);
  } catch (err) {
    showError(err, () => void loadSample());
  }
}

wire();
switchTab(activeTab);
clearLog();
log.system(`GGUFScope ready — ${eventCount()} events`);
log.system('Nothing is uploaded. Remote inspection reads a few MB of a public file straight from Hugging Face.');

// A repo or URL can be deep-linked: ?m=Qwen/Qwen3-8B-GGUF
const initial = new URLSearchParams(location.search).get('m');
if (initial) {
  refs.addrInput.value = initial;
  void startFromAddress();
}
